import React, { useState, useEffect, useCallback } from "react";

import { useFormik } from "formik";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

import { migraUsuarioPasoGet, migraPasoGet } from "services/services-mia/migra";
import { useService } from "components/route/ServiceRoute";
import { log } from "utils/logger";

import XGridServer from "components/UI/Grid/XGridServer";
import Filters from "components/Filters";
import Modal from "components/UI/Modal/Modal";
import { Buttons, HeaderButton } from "components";
import { SemaforoModal, VueloModal, VueloDetalleModal } from "./Actions";

import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import { Cached, EditRounded, AirlineSeatReclineExtraRounded } from "@mui/icons-material";

import { Routes } from "consts/Routes-App";
import { migraUsuarioPasoColumns, mapMigraUsuarioPasoRow } from "pages/migra/utils/usuarioPaso";

import s from "styles/pages/Pages.module.scss";

const INCLUIR_VENCIDOS_OPTIONS = [
    { cod: "", nombre: "Todos" },
    { cod: "Y", nombre: "Sí" },
    { cod: "N", nombre: "No" },
];

export default function UsuariosPaso() {
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [forceUpload, setForceUpload] = useState(false);
    const [modalSemaforo, setModalSemaforo] = useState({ isOpen: false });
    const [modalVuelo, setModalVuelo] = useState({ isOpen: false });
    const [modalVueloDetalle, setModalVueloDetalle] = useState({ isOpen: false });

    const [pasos, setPasos] = useState([]);
    const [loadingPasos, setLoadingPasos] = useState(true);
    const [errorPasos, setErrorPasos] = useState(null);

    const { service } = useService();
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const updateList = useCallback(() => {
        setForceUpload(Date.now());
    }, []);

    const formik = useFormik({
        initialValues: {
            paso: "",
            usuarioApellidos: "",
            usuarioNroDeDocumentoX: "",
            incluirVencidos: "",
            fechaDesde: "",
            fechaHasta: "",
        },
        onSubmit: () => updateList(),
    });

    const resetFilters = () => {
        formik.resetForm();
        updateList();
    };

    const getRows = async (page, pageSize) => {
        const { result, total } = await migraUsuarioPasoGet(
            {
                migraPasoId: formik.values.paso || null,
                usuarioApellidos: formik.values.usuarioApellidos || null,
                usuarioNroDeDocumentoX: formik.values.usuarioNroDeDocumentoX || null,
                incluirVencidos: formik.values.incluirVencidos || null,
                fechaDesde: formik.values.fechaDesde || null,
                fechaHasta: formik.values.fechaHasta || null,
                page: page + 1,
                pageSize,
            },
            credentials
        );

        const rows = (result || []).map(mapMigraUsuarioPasoRow);

        return { rows, rowCount: total };
    };

    useEffect(() => {
        setRoute(Routes.migraUsuarioPasoTable);
    }, [setRoute]);

    useEffect(() => {
        if (!credentials?.merchantId || !service) return;
        updateList();
    }, [service, credentials, updateList]);

    useEffect(() => {
        if (!credentials?.merchantId) return;

        const fetchPasos = async () => {
            setLoadingPasos(true);
            try {
                const { result } = await migraPasoGet({}, credentials);
                const pasosActivos = (result || []).filter(p => p.migraPasoStatus !== "B");

                setPasos(pasosActivos);
                setErrorPasos(null);
            } catch (err) {
                log.error("Error cargando pasos:", err);
                setErrorPasos("No se pudieron cargar los pasos disponibles");
            } finally {
                setLoadingPasos(false);
            }
        };

        fetchPasos();
    }, [credentials]);

    const closeSemaforoModal = () => setModalSemaforo({ isOpen: false });
    const closeVueloModal = () => setModalVuelo({ isOpen: false });
    const closeVueloDetalleModal = () => setModalVueloDetalle({ isOpen: false });

    return (
        <main className={s.main}>
            <Modal
                open={modalSemaforo.isOpen}
                title="Actualizar Semáforo"
                onClose={closeSemaforoModal}
            >
                {modalSemaforo.isOpen && (
                    <SemaforoModal
                        row={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeSemaforoModal}
                        credentials={credentials}
                        setRowSelected={setRowSelected}
                    />
                )}
            </Modal>
            <Modal
                open={modalVuelo.isOpen}
                title="Asignar Vuelo"
                onClose={closeVueloModal}
            >
                {modalVuelo.isOpen && (
                    <VueloModal
                        row={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeVueloModal}
                        credentials={credentials}
                        setRowSelected={setRowSelected}
                    />
                )}
            </Modal>
            <Modal
                open={modalVueloDetalle.isOpen}
                title="Detalle del Vuelo"
                onClose={closeVueloDetalleModal}
                wide
            >
                {modalVueloDetalle.isOpen && (
                    <VueloDetalleModal
                        row={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeVueloDetalleModal}
                        credentials={credentials}
                        setRowSelected={setRowSelected}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "select",
                                name: "paso",
                                label: loadingPasos ? "Cargando pasos..." : "Paso",
                                width: "220px",
                                disabled: loadingPasos || !!errorPasos,
                                options: [{ migraPasoId: "", migraPasoNombre: "Todos" }, ...pasos],
                                firstOptionKey: "migraPasoId",
                                firstOptionValue: "migraPasoId",
                                firstOptionName: "migraPasoNombre",
                            },
                            { type: "text", name: "usuarioApellidos", label: "Apellidos", disabled: false },
                            { type: "text", name: "usuarioNroDeDocumentoX", label: "Documento", disabled: false },
                            {
                                type: "select",
                                name: "incluirVencidos",
                                label: "Incluir vencidos",
                                disabled: false,
                                options: INCLUIR_VENCIDOS_OPTIONS,
                                firstOptionKey: "cod",
                                firstOptionName: "nombre",
                                firstOptionValue: "cod",
                            },
                            { type: "date", name: "fechaDesde", label: "Fecha desde", disabled: false },
                            { type: "date", name: "fechaHasta", label: "Fecha hasta", disabled: false },
                        ]}
                    />
                    <HeaderButton text={"Limpiar filtros"} onClick={resetFilters}>
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton text="Refrescar" onClick={updateList}>
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text="Actualizar semáforo"
                        onClick={() => setModalSemaforo({ isOpen: true })}
                        disabled={!rowSelected.row.id || rowSelected.row.migraUsuarioPasoEstado !== "P"}
                    >
                        <EditRounded />
                    </HeaderButton>
                    <HeaderButton
                        text="Asignar vuelo"
                        onClick={() => setModalVuelo({ isOpen: true })}
                        disabled={!rowSelected.row.id || rowSelected.row.migraUsuarioPasoEstado !== "P"}
                    >
                        <AirlineSeatReclineExtraRounded />
                    </HeaderButton>
                    {/* <HeaderButton
                        text="Ver vuelo"
                        onClick={() => setModalVueloDetalle({ isOpen: true })}
                        disabled={!rowSelected.row.migraVueloId}
                    >
                        <AirplanemodeActiveRounded />
                    </HeaderButton> */}
                </Buttons>
            </div>
            <div className={s.container}>
                <XGridServer
                    titles={migraUsuarioPasoColumns}
                    updateList={getRows}
                    forceUpload={forceUpload}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
